import axios from 'axios'
import React, { useState, useContext } from 'react'
import { UserContext } from '../../../Contexts/UserContext'
import AlertDialog from '../../../components/AlertDialog'
import ConfirmDialog from '../../../components/ConfirmDialog'
import { baseUrl } from '../../../baseUrl'

function PromotionWD() {
    const [programme, setProgramme] = useState("UG")
    const [ugType, setUgType] = useState("BTech")
    const [category, setCategory] = useState("promote")
    const [yearState, setYearState] = useState("1")
    const [previewStudents, setPreviewStudents] = useState([])
    const [showPreview, setShowPreview] = useState(false)
    const [previewLoading, setPreviewLoading] = useState(false)
    
    const { setLoading } = useContext(UserContext);

    const [open1, setOpen1] = useState("");
    const [open2, setOpen2] = useState("");
    const [modalText, setModalText] = useState("");
    const [modalHeading, setModalHeading] = useState("");

    // Dynamic year options based on programme and UG type (KTU structure)
    const getYearOptions = () => {
        if (programme === "UG") {
            if (ugType === "BTech") {
                return [
                    { value: "1", label: "S1 & S2 (First Year)" },
                    { value: "2", label: "S3 & S4 (Second Year)" },
                    { value: "3", label: "S5 & S6 (Third Year)" },
                    { value: "4", label: "S7 & S8 (Fourth Year)" }
                ];
            } else if (ugType === "BArch") {
                return [
                    { value: "1", label: "S1 & S2 (First Year)" },
                    { value: "2", label: "S3 & S4 (Second Year)" },
                    { value: "3", label: "S5 & S6 (Third Year)" },
                    { value: "4", label: "S7 & S8 (Fourth Year)" },
                    { value: "5", label: "S9 & S10 (Fifth Year)" }
                ];
            }
        } else if (programme === "PG") {
            return [
                { value: "1", label: "S1 & S2 (First Year)" },
                { value: "2", label: "S3 & S4 (Second Year)" }
            ];
        } else if (programme === "Phd") {
            return [
                { value: "1", label: "First Year" },
                { value: "2", label: "Second Year" },
                { value: "3", label: "Third Year" },
                { value: "4", label: "Fourth Year" },
                { value: "5", label: "Fifth Year" },
                { value: "6", label: "Sixth Year" }
            ];
        }
        return [];
    };

    // Get max year for current programme/type (KTU structure)
    const getMaxYear = () => {
        if (programme === "UG") {
            return ugType === "BTech" ? 4 : 5;
        } else if (programme === "PG") {
            return 2;
        } else if (programme === "Phd") {
            return 6;
        }
        return 4;
    };

    // Get year label (KTU semester system)
    const getYearLabel = (yearNum) => {
        if (programme === "UG" || programme === "PG") {
            const yearLabels = [
                "S1 & S2 (First Year)", 
                "S3 & S4 (Second Year)", 
                "S5 & S6 (Third Year)", 
                "S7 & S8 (Fourth Year)", 
                "S9 & S10 (Fifth Year)"
            ];
            return yearLabels[yearNum - 1] || `Year ${yearNum}`;
        } else {
            const yearLabels = ["First Year", "Second Year", "Third Year", "Fourth Year", "Fifth Year", "Sixth Year"];
            return yearLabels[yearNum - 1] || `Year ${yearNum}`;
        }
    };

    // Reset year when programme or UG type changes
    const handleProgrammeChange = (e) => {
        setProgramme(e.target.value);
        setYearState("1");
        setShowPreview(false);
        setPreviewStudents([]);
    };

    const handleUgTypeChange = (e) => {
        setUgType(e.target.value);
        setYearState("1");
        setShowPreview(false);
        setPreviewStudents([]);
    };

    const handleYearChange = (e) => {
        setYearState(e.target.value);
        setShowPreview(false);
        setPreviewStudents([]);
    };

    const handleCategoryChange = (e) => {
        setCategory(e.target.value);
        setShowPreview(false);
        setPreviewStudents([]);
    };

    // Function to preview students
    const handlePreviewStudents = async () => {
        setPreviewLoading(true);
        
        try {
            const requestData = {
                programme: programme,
                year: yearState,
                ...(programme === "UG" && { ugType: ugType })
            };

            const response = await axios.post(`${baseUrl}/warden/preview-students`, requestData);
            setPreviewStudents(response.data.students || []);
            setShowPreview(true);
        } catch (error) {
            console.error('Error fetching preview:', error);
            setModalHeading("Error");
            setModalText("Failed to fetch student preview. Please try again.");
            setOpen1(true);
        } finally {
            setPreviewLoading(false);
        }
    };

    // Function to handle form submission and show confirm dialog
    const handleFormSubmit = (e) => {
        e.preventDefault();
        
        const currentYearNum = parseInt(yearState);
        const maxYear = getMaxYear();
        
        if ((category === "promote" && currentYearNum >= maxYear) || 
            (category === "demote" && currentYearNum <= 1)) {
            return;
        }

        let confirmMessage = "";
        if (category === "promote") {
            confirmMessage = `You are about to promote ${previewStudents.length} ${programme}${programme === "UG" ? ` (${ugType})` : ""} students from ${getYearLabel(currentYearNum)} to ${getYearLabel(currentYearNum + 1)}. This action cannot be undone. Do you want to continue?`;
        } else {
            confirmMessage = `You are about to demote ${previewStudents.length} ${programme}${programme === "UG" ? ` (${ugType})` : ""} students from ${getYearLabel(currentYearNum)} to ${getYearLabel(currentYearNum - 1)}. This action cannot be undone. Do you want to continue?`;
        }

        setModalHeading("Confirm Action");
        setModalText(confirmMessage);
        setOpen2(true);
    };

    // Function to execute the actual API call
    const handleSubmit = async () => {
        setLoading(true)
        
        const requestData = {
            programme: programme,
            year: yearState,
            category: category,
            ...(programme === "UG" && { ugType: ugType })
        };

        try {
            const response = await axios.post(`${baseUrl}/warden/promote-student`, requestData);
            setLoading(false);
            setModalHeading("Request Completed");
            setModalText(`Operation completed successfully. ${response.data.students?.length || 0} students have been ${category}d.`);
            setOpen1(true);
            
            // Reset preview after successful operation
            setShowPreview(false);
            setPreviewStudents([]);
        } catch (error) {
            console.log('err', error);
            setLoading(false);
            setModalHeading("Error");
            setModalText(error.response?.data?.message || "An error occurred. Please try again.");
            setOpen1(true);
        }
    }

    const yearOptions = getYearOptions();
    const maxYear = getMaxYear();
    const currentYearNum = parseInt(yearState);
    const isValidOperation = !((category === "promote" && currentYearNum >= maxYear) || 
                              (category === "demote" && currentYearNum <= 1));

    return (
        <div>
            <form onSubmit={handleFormSubmit}>
                <div className="grid grid-cols-2 w-100 gap-4 mb-3">
                    <label htmlFor="">Category :</label>
                    <select
                        name="category"
                        value={category}
                        onChange={handleCategoryChange}
                        className="p-3 ring-slate-200 ring-2 rounded-xl outline-none"
                        required
                    >
                        <option value="promote">Promote</option>
                        <option value="demote">Demote</option>
                    </select>
                </div>

                <div className="grid grid-cols-2 w-100 gap-4 mb-3">
                    <label htmlFor="">Programme :</label>
                    <select
                        name="programme"
                        value={programme}
                        onChange={handleProgrammeChange}
                        className="p-3 ring-slate-200 ring-2 rounded-xl outline-none"
                        required
                    >
                        <option value="UG">UG (Under Graduate)</option>
                        <option value="PG">PG (Post Graduate)</option>
                        <option value="Phd">PhD (Doctor of Philosophy)</option>
                    </select>
                </div>

                {programme === "UG" && (
                    <div className="grid grid-cols-2 w-100 gap-4 mb-3">
                        <label htmlFor="">UG Type :</label>
                        <select
                            name="ugType"
                            value={ugType}
                            onChange={handleUgTypeChange}
                            className="p-3 ring-slate-200 ring-2 rounded-xl outline-none"
                            required
                        >
                            <option value="BTech">B.Tech (4 Years - 8 Semesters)</option>
                            <option value="BArch">B.Arch (5 Years - 10 Semesters)</option>
                        </select>
                    </div>
                )}

                <div className="grid grid-cols-2 w-100 gap-4 mb-3">
                    <label htmlFor="">Year :</label>
                    <select
                        name="year"
                        value={yearState}
                        onChange={handleYearChange}
                        className="p-3 ring-slate-200 ring-2 rounded-xl outline-none"
                        required
                    >
                        {yearOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Preview Students Button */}
                <div className="w-full flex items-center justify-center mb-4">
                    <button 
                        type="button"
                        onClick={handlePreviewStudents}
                        disabled={!isValidOperation || previewLoading}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                    >
                        {previewLoading ? "Loading..." : "Preview Students"}
                    </button>
                </div>

                {/* Students Preview Table */}
                {showPreview && (
                    <div className="mb-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                        <h3 className="font-bold text-lg mb-3">
                            Students to be {category}d ({previewStudents.length} total)
                        </h3>
                        {previewStudents.length > 0 ? (
                            <div className="max-h-60 overflow-y-auto">
                                <table className="w-full text-sm border-collapse">
                                    <thead className="sticky top-0 bg-gray-100">
                                        <tr>
                                            <th className="border border-gray-300 p-2 text-left">Admission No</th>
                                            <th className="border border-gray-300 p-2 text-left">Name</th>
                                            <th className="border border-gray-300 p-2 text-left">Department</th>
                                            <th className="border border-gray-300 p-2 text-left">Current Year</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {previewStudents.map((student, index) => (
                                            <tr key={index} className="hover:bg-gray-50">
                                                <td className="border border-gray-300 p-2">{student.admission_no}</td>
                                                <td className="border border-gray-300 p-2">{student.name}</td>
                                                <td className="border border-gray-300 p-2">{student.department}</td>
                                                <td className="border border-gray-300 p-2">{student.current_year}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p className="text-gray-600">No students found for the selected criteria.</p>
                        )}
                    </div>
                )}

                {/* Preview message */}
                {category === "promote" && currentYearNum < maxYear && showPreview && previewStudents.length > 0 && (
                    <p className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <strong>Promotion Preview:</strong> {previewStudents.length} students from {getYearLabel(currentYearNum)} will be promoted to {getYearLabel(currentYearNum + 1)}
                        {programme !== "Phd" && (
                            <span className="block text-sm text-green-700 mt-1">
                                KTU semester system: Moving to next academic year
                            </span>
                        )}
                    </p>
                )}
                
                {category === "demote" && currentYearNum > 1 && showPreview && previewStudents.length > 0 && (
                    <p className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <strong>Demotion Preview:</strong> {previewStudents.length} students from {getYearLabel(currentYearNum)} will be demoted to {getYearLabel(currentYearNum - 1)}
                        {programme !== "Phd" && (
                            <span className="block text-sm text-red-700 mt-1">
                                KTU semester system: Moving back to previous academic year
                            </span>
                        )}
                    </p>
                )}

                {/* Warning messages */}
                {category === "promote" && currentYearNum >= maxYear && (
                    <p className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800">
                        <strong>Note:</strong> Students in final year cannot be promoted further.
                        {programme === "UG" && ugType === "BTech" && (
                            <span className="block text-sm mt-1">B.Tech students complete their degree after S8 (4th year)</span>
                        )}
                        {programme === "UG" && ugType === "BArch" && (
                            <span className="block text-sm mt-1">B.Arch students complete their degree after S10 (5th year)</span>
                        )}
                        {programme === "PG" && (
                            <span className="block text-sm mt-1">PG students complete their degree after S4 (2nd year)</span>
                        )}
                    </p>
                )}

                {category === "demote" && currentYearNum <= 1 && (
                    <p className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800">
                        <strong>Note:</strong> First year students cannot be demoted further.
                    </p>
                )}

                <div className="w-full flex items-end justify-end mt-5">
                    <button 
                        className="ml-auto p-3 bg-stone-800 text-white rounded-xl hover:bg-stone-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                        type="submit"
                        disabled={!isValidOperation || !showPreview || previewStudents.length === 0}
                    >
                        Submit {category === "promote" ? "Promotion" : "Demotion"}
                    </button>
                </div>
            </form>

            <AlertDialog
                open={open1}
                setOpen={setOpen1}
                modalHeading={modalHeading}
                modalText={modalText}
            />
            
            <ConfirmDialog
                open={open2}
                setOpen={setOpen2}
                modalHeading={modalHeading}
                modalText={modalText}
                confirmFunction={handleSubmit}
            />
        </div>
    )
}

export default PromotionWD